import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface BooleanFilterProps {
  label: string;
  selectedValue: boolean | null;
  onFilterChange: (value: boolean | null) => void;
  placeholder?: string;
  trueLabel?: string;
  falseLabel?: string;
}

const BooleanFilter: React.FC<BooleanFilterProps> = ({
  label,
  selectedValue,
  onFilterChange,
  placeholder = 'All',
  trueLabel = 'Yes',
  falseLabel = 'No',
}) => {
  const { colors } = useTheme();
  const [isEnabled, setIsEnabled] = useState<boolean | null>(selectedValue);

  useEffect(() => {
    setIsEnabled(selectedValue);
  }, [selectedValue]);

  const handleToggle = () => {
    // Cycle through: null (All) -> true (Yes) -> false (No) -> null (All)
    let newValue: boolean | null;
    if (isEnabled === null) {
      newValue = true;
    } else if (isEnabled === true) {
      newValue = false;
    } else {
      newValue = null;
    }
    setIsEnabled(newValue);
    onFilterChange(newValue);
  };

  const handleClear = () => {
    setIsEnabled(null);
    onFilterChange(null);
  };

  const displayText = () => {
    if (isEnabled === null) {
      return placeholder;
    }
    return isEnabled ? trueLabel : falseLabel;
  };

  return (
    <div className="relative">
      <label className={`block text-xs font-medium ${colors.textSecondary} mb-2`}>{label}</label>
      <div className={`${colors.surfaceHover} rounded-lg p-3 border ${colors.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <button
              type="button"
              onClick={handleToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                isEnabled === true
                  ? 'bg-indigo-600'
                  : isEnabled === false
                  ? 'bg-red-400'
                  : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={isEnabled !== null}
              aria-label={`Filter: ${displayText()}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                  isEnabled === true
                    ? 'translate-x-6'
                    : isEnabled === false
                    ? 'translate-x-1'
                    : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium min-w-[60px] ${
              isEnabled === null 
                ? colors.textMuted
                : isEnabled === true 
                ? 'text-indigo-700' 
                : 'text-red-700'
            }`}>
              {displayText()}
            </span>
          </div>
          {isEnabled !== null && (
            <button
              type="button"
              onClick={handleClear}
              className={`p-1 ${colors.textMuted} hover:${colors.textSecondary} transition-colors`}
              title="Clear"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BooleanFilter;

